import clsx from 'clsx'
import { FC, useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import {
  EMPTY,
  catchError,
  finalize,
  from,
  takeUntil
} from 'rxjs'
import { CampaignSubmissionApi, PeopleApi } from 'src/api'
import { Button, Checkbox, DialogConfirmUnMatch, Tooltip, UserAvatar, useAnalytic } from 'src/components'
import { MenuDropdown } from 'src/components/menu-dropdown'
import { CAMPAIGN_SUBMISSION_STATUS_TEXT } from 'src/constants/campaign-submission'
import { ECampaignSubmissionStatus, ETrackingEvent } from 'src/enums'
import { useAsRef, useUnsubscribe } from 'src/hooks'
import { IconChat, IconClose, IconEmojiGreatMatch, IconEmojiNotAMatch, IconEmojiPossibleMatch, IconEmojiToReview, IconSearch, IconThumbsUp, IconThumbsUpDup, IconThumbsUpFilled } from 'src/icons'
import { ICampaignSubmissionModel } from 'src/interfaces'
import { ModalApplicantConfirmPassed } from 'src/partials/modal-applicant-confirm-passed'
import { mustVerifiedCompany } from 'src/partials/modal-verify-company'
import { ModalViewVideo } from 'src/partials/modal-view-video'
import { VideoPlayerTracking } from 'src/partials/video-player-tracking'
import { ERoutes, generate } from 'src/router'
import { DialogService, SnackbarService } from 'src/services'
import { LoadingModule } from 'src/store'
import {
  ReactionUtils,
  StyleUtils,
  VideoUtils,
  getAvatar,
  getFullName,
  renderVideoTitle
} from 'src/utils'
import Style from './style.module.scss'

interface IProps {
  submission: ICampaignSubmissionModel
  checked?: boolean
  tab?: ECampaignSubmissionStatus
  onChange?: (id: number, values: Partial<ICampaignSubmissionModel>) => void
  onDetail?: () => void
  onSelectedItem?: () => void
  onRemove?: (ids: number[]) => void
  countSubmission?: () => void
}

export const CampaignApplicantCard: FC<IProps> = ({
  submission,
  checked,
  tab,
  onChange,
  onDetail,
  onSelectedItem,
  onRemove,
  countSubmission
}) => {
  const history = useHistory()
  const unsubscribe$ = useUnsubscribe()
  const onChangeRef = useAsRef(onChange)
  const { eventHandler } = useAnalytic('job_detail')

  const [loading, setLoading] = useState(false)
  const [showPreviewVideo, setShowPreviewVideo] = useState(false)

  const video = useMemo(() => submission?.video, [submission?.video])
  const reaction = useMemo(() => submission.peopleReaction, [submission.peopleReaction])
  const lookupId = useMemo(() => submission?.author?.lookupId, [submission?.author?.lookupId])
  const isEmphasis = useMemo(() => ReactionUtils.isEmphasis(reaction), [reaction])

  const badgeStatus: Partial<Record<ECampaignSubmissionStatus, { label: string; color: string }>> = {
    [ECampaignSubmissionStatus.GREAT_MATCH]: {
      label: 'Great Match',
      color: '#78E59E'
    },
    [ECampaignSubmissionStatus.POSSIBLE_MATCH]: {
      label: 'Could Work',
      color: '#97EBF2'
    },
    [ECampaignSubmissionStatus.NOT_A_MATCH]: {
      label: 'Pass for Now',
      color: '#FDD9D5'
    },
    [ECampaignSubmissionStatus.TO_REVIEW]: {
      label: 'To Review',
      color: '#FFCF99'
    },
    [ECampaignSubmissionStatus.PASSED]: {
      label: 'Passed',
      color: '#F8867B'
    }
  }

  const onVideo = useCallback(() => {
    if (onDetail) {
      return onDetail()
    }

    video && DialogService.open(ModalViewVideo, { video })
  }, [onDetail, video])

  const onToggleLike = useCallback(() => {
    if (!submission.authorId) return

    setLoading(true)
    from(PeopleApi.toggleLike({ id: submission.authorId }))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => setLoading(false))
      )
      .subscribe(({ data }) => {
        onChangeRef.current?.(submission?.id, { peopleReaction: data || null })
      })
  }, [submission.authorId, submission?.id, unsubscribe$, onChangeRef])

  const onReview = useCallback((status: ECampaignSubmissionStatus) => {
    if (!submission.authorId) return

    LoadingModule.toggle(true)
    from(CampaignSubmissionApi.review(submission.id, { status }))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => LoadingModule.toggle(false))
      )
      .subscribe(() => {
        submission?.id && onRemove?.([submission?.id])
        countSubmission?.()
        SnackbarService.success(`Applicant(s) added to ${CAMPAIGN_SUBMISSION_STATUS_TEXT[status as ECampaignSubmissionStatus]}.`)
      })
  }, [submission, unsubscribe$, onRemove, countSubmission])

  const onPass = useCallback((rejectionReason?: string) => {
    if (!submission.authorId) return

    LoadingModule.toggle(true)
    from(CampaignSubmissionApi.pass(submission.id, { rejectionReason }))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => LoadingModule.toggle(false))
      )
      .subscribe(() => {
        submission?.id && onRemove?.([submission?.id])
        countSubmission?.()
        SnackbarService.success('Applicant(s) added to Passed.')
      })
  }, [submission, unsubscribe$, onRemove, countSubmission])

  return (
    <div key={submission.id} className={Style.campaignContainer}>
      <div className="w-full relative bg-neutral-900 fx-ai-center fx-jc-center fx-row round-4 overflow-hidden z-0">
        <div className="absolute b-8 z-3 w-full fx fx-ai-center fx-jc-space-between gap-4 px-4">
          <div className="fx fx-ai-center gap-4">
            <UserAvatar
              size={32}
              className="round-10"
              fullName={getFullName(submission?.author)}
              avatarUrl={getAvatar(submission?.author)}
            />

            <div>
              {getFullName(submission?.author) && <div className="semibold-14 txt-neutral-white three-dot-1">{getFullName(submission?.author)}</div>}
              {submission?.author?.username && (
                <div className="body-12 txt-neutral-white three-dot-1">
                  @{submission?.author?.username}
                </div>
              )}
            </div>
          </div>

          {onToggleLike && (
            <div className="fx fx-ai-center gap-3">
              <Button
                size={32}
                variant="icon"
                green={!!reaction}
                emphasis={isEmphasis}
                // disabled={loading}
                onClick={eventHandler(ETrackingEvent.BTN_TALENT_LIKE, () => !loading && (
                  isEmphasis
                    ? DialogService.open(DialogConfirmUnMatch, {
                      name: getFullName(submission?.author),
                      onConfirm: onToggleLike
                    })
                    : mustVerifiedCompany(onToggleLike)
                ))}
              >
                {!reaction
                  ? <IconThumbsUp size={20}/>
                  : isEmphasis
                    ? <IconThumbsUpDup size={20}/>
                    : <IconThumbsUpFilled size={20}/>}
              </Button>

              {lookupId && (
                <Button
                  size={32}
                  variant="icon"
                  onClick={eventHandler(ETrackingEvent.BTN_TALENT_MESSAGE, () => history.push(generate(ERoutes.TALENT_MESSAGES, { lookupId })))}
                >
                  <IconChat size={20}/>
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="absolute z-3" style={{ top: 8, right: 8 }}>
          <Checkbox checked={checked} onClick={onSelectedItem}/>
        </div>

        <div
          className="w-full pointer"
          style={{
            aspectRatio: '9/16',
            ...StyleUtils.backgroundCover(video?.urlVideoImageThumbnail)
          }}
          onMouseEnter={() => setShowPreviewVideo(true)}
          onMouseLeave={() => setShowPreviewVideo(false)}
          onClick={onVideo}
        >
          {showPreviewVideo && (
            <VideoPlayerTracking
              videoId={video?.id}
              mini
              autoplay
              hideControl
              hidePlayIcon
              hideStartEnd
              url={VideoUtils.getVideoSource(video) ?? ''}
              tracks={video?.tracks}
              image={video?.urlVideoImageThumbnail}
            />
          )}
        </div>
      </div>

      <div className="bold-14 txt-black-01 three-dot-1 text-center">{renderVideoTitle(submission?.video)}</div>

      <div className="fx fx-ai-center gap-3">
        {onDetail && (
          <Button
            onClick={eventHandler(ETrackingEvent.BTN_TALENT_DETAILS, onVideo)}
          >
            Details
          </Button>
        )}

        {![ECampaignSubmissionStatus.ALL, ECampaignSubmissionStatus.PASSED].includes(tab as ECampaignSubmissionStatus) && (
          <MenuDropdown
            placement="top"
            dropdownButtonClassName="p-0"
            dropdownMenuStyle={{ left: 0, right: 'unset' }}
            dropdownRender={(
              <div className={Style.dropdown}>
                {tab !== ECampaignSubmissionStatus.GREAT_MATCH && (
                  <Tooltip title="This Applicant is great for the role.">
                    <div className={clsx('fx-column fx-ai-center gap-2 p-1 pointer', Style.greatMatch)} onClick={() => onReview(ECampaignSubmissionStatus.GREAT_MATCH)}>
                      <IconEmojiGreatMatch/>
                      <div style={{ minWidth: 'max-content' }} className="fs-12 fw-500 txt-black-02">Great Match</div>
                    </div>
                  </Tooltip>
                )}

                {tab !== ECampaignSubmissionStatus.POSSIBLE_MATCH && (
                  <Tooltip title="This Applicant might be a fit.">
                    <div className={clsx('fx-column fx-ai-center gap-2 p-1 pointer', Style.possibleMatch)} onClick={() => onReview(ECampaignSubmissionStatus.POSSIBLE_MATCH)}>
                      <IconEmojiPossibleMatch/>
                      <div style={{ minWidth: 'max-content' }} className="fs-12 fw-500 txt-black-02">Possible Match</div>
                    </div>
                  </Tooltip>
                )}

                {tab !== ECampaignSubmissionStatus.NOT_A_MATCH && (
                  <Tooltip title="This Applicant is not a fit.">
                    <div className={clsx('fx-column fx-ai-center gap-2 p-1 pointer', Style.notAMatch)} onClick={() => onReview(ECampaignSubmissionStatus.NOT_A_MATCH)}>
                      <IconEmojiNotAMatch/>
                      <div style={{ minWidth: 'max-content' }} className="fs-12 fw-500 txt-black-02">Not a Match</div>
                    </div>
                  </Tooltip>
                )}

                {tab !== ECampaignSubmissionStatus.TO_REVIEW && (
                  <Tooltip title="Reconsider this Applicant later.">
                    <div className={clsx('fx-column fx-ai-center gap-2 p-1 pointer', Style.toReview)} onClick={() => onReview(ECampaignSubmissionStatus.TO_REVIEW)}>
                      <IconEmojiToReview/>
                      <div style={{ minWidth: 'max-content' }} className="fs-12 fw-500 txt-black-02">To Review</div>
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
          >
            <Button
              variant="secondary"
              className="fx fx-ai-center gap-2"
              style={{ backgroundColor: '#72DA97' }}
            >
              <IconSearch size={20}/>
              Review
            </Button>
          </MenuDropdown>
        )}

        {tab === ECampaignSubmissionStatus.NOT_A_MATCH && (
          <Tooltip title="Pass this applicant">
            <Button
              variant="secondary"
              onClick={() => DialogService.open(ModalApplicantConfirmPassed, { action: onPass })}
              style={{ backgroundColor: '#FBC3BC' }}
            >
              <div className="fx fx-center" style={{ height: 16 }}>
                <IconClose size={20}/>
              </div>
              Pass
            </Button>
          </Tooltip>
        )}

        {submission?.status && tab === ECampaignSubmissionStatus.ALL && (
          <Button
            variant="positive"
            style={{ backgroundColor: badgeStatus[submission?.status as ECampaignSubmissionStatus]?.color }}
          >
            {badgeStatus[submission?.status as ECampaignSubmissionStatus]?.label}
          </Button>
        )}
      </div>
    </div>
  )
}
